import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NotificationService, UploadService, AmplitudeService, UserConfigService, AuthenticationService } from '@core/services';
import { interval, Subscription } from 'rxjs';
import { LoadingService } from '@core/component/global-loading/loading.service';
import { BattlecardsDemoComponent, MainWrapperComponent } from '@core/component';

interface FileMetadata {
  id?: string;
  name: string;
  storagePath: string;
  note: string;
  size?: number;
  type?: string;
  status?: string;
  error_message?: string;
  isUploading?: boolean;
  uploadError?: string;
  originalFile?: File;
  originalNote?: string;
}

@Component({
  selector: 'app-upload',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, BattlecardsDemoComponent, MainWrapperComponent],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
})
export class UploadComponent implements OnInit, OnDestroy {
  @ViewChild('battlecardsDemo') battlecardsDemo!: BattlecardsDemoComponent;
  private statusPolling?: Subscription;
  private readonly statusPollInterval = 5000;
  readonly MAX_PDFS = 3;
  uploadedFiles: FileMetadata[] = [];
  showDeleteConfirmation = false;
  fileToDelete: FileMetadata | null = null;
  isUploading = false;
  pendingUploads: FileMetadata[] = [];

  // Web Sources
  webSources: string[] = [];
  newWebSource = '';
  userDefaultDomain: string | null = null;
  showFreeEmailTooltip = false;
  isSavingSources = false;
  
  private readonly FREE_EMAIL_DOMAINS = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'aol.com', 'outlook.com', 
    'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com'
  ];

  constructor(
    private notificationService: NotificationService,
    private uploadService: UploadService,
    private _amplitudeService: AmplitudeService,
    private userConfigService: UserConfigService,
    private loadingService: LoadingService,
    private authService: AuthenticationService
  ) {}

  async ngOnInit(): Promise<void> {
    const isValid = await this.authService.ensureValidSession();
    if (isValid) {
      await this.loadFiles({ showLoader: true });
      this.userDefaultDomain = this.userConfigService.getUserDefaultDomain();
      await this.loadWebSources();
    }
  }

  ngOnDestroy() {
    this.statusPolling?.unsubscribe();
  }

  openBattlecardsDemo(): void {
    if (!this.battlecardsDemo) {
      return;
    }

    this.battlecardsDemo.open();
  }

  private async loadFiles(options: { showLoader?: boolean } = {}): Promise<void> {
    const isValid = await this.authService.ensureValidSession();
    if (!isValid) {
      return;
    }

    const { showLoader = false } = options;

    if (showLoader) {
      this.loadingService.show();
    }

    try {
      const files = await this.uploadService.getFiles();
      this.uploadedFiles = files.map(file => ({
        id: file.id,
        name: file.file_name || this.getDisplayName(file.path),
        storagePath: file.path,
        note: '',
        size: file.size,
        type: file.type,
        status: file.status,
        error_message: file.error_message
      }));
    } catch (error: any) {
      console.error('Error loading files:', error);
      if (error.message?.includes('No active session')) {
        console.warn('Session expired, user needs to re-authenticate');
      } else {
        this.notificationService.notify('Error loading files');
      }
    } finally {
      if (showLoader) {
        this.loadingService.hide();
      }
      this.syncStatusPolling();
    }
  }

  private getDisplayName(path: string): string {
    const filename = path.split('/').pop() || '';
    return filename.replace(/^\d+-/, '');
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.handleFiles(files);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.handleFiles(input.files);
    }
    // Reset so selecting the same file(s) again still fires (change).
    input.value = '';
  }

  private isPdf(file: File): boolean {
    const name = (file.name || '').toLowerCase();
    return file.type === 'application/pdf' || name.endsWith('.pdf');
  }

  private currentFileCount(): number {
    return this.uploadedFiles.length + this.pendingUploads.filter(p => p.status !== 'error').length;
  }

  private handleFiles(fileList: FileList | File[]): void {
    const files = Array.from(fileList);
    const pdfs = files.filter(f => this.isPdf(f));

    if (pdfs.length < files.length) {
      this.notificationService.notify('Only PDF files can be uploaded.');
    }
    if (!pdfs.length) return;

    const remaining = this.MAX_PDFS - this.currentFileCount();
    if (remaining <= 0) {
      this.notificationService.notify(`You can upload up to ${this.MAX_PDFS} PDFs.`);
      return;
    }

    const accepted = pdfs.slice(0, remaining);
    if (pdfs.length > remaining) {
      this.notificationService.notify(`You can upload up to ${this.MAX_PDFS} PDFs. Only ${remaining} more can be added.`);
    }

    accepted.forEach(file => this.handleFileSelection(file));
  }

  handleFileSelection(file: File): void {
    this._amplitudeService.trackEvent('Upload_AddFile', {
      file_name: file.name,
      file_type: file.type || file.name.split('.').pop() || 'unknown',
      size: this.formatFileSize(file.size),
      user_id: this.getUserId()
    });

    const displayName = this.getDisplayName(file.name);
    const defaultNote = 'Uploaded via quick upload';
    const sizeLabel = this.formatFileSize(file.size || 0);

    const optimisticEntry: FileMetadata = {
      name: displayName,
      storagePath: '',
      note: `Uploading... (${sizeLabel})`,
      size: file.size,
      type: file.type,
      status: 'uploading',
      isUploading: true,
      originalFile: file,
      originalNote: defaultNote
    };

    this.pendingUploads = [optimisticEntry, ...this.pendingUploads];
    this.syncStatusPolling();
    void this._performUpload(file, displayName, defaultNote);
  }

  private async _performUpload(file: File, displayName: string, note: string): Promise<void> {
    const isValid = await this.authService.ensureValidSession();
    if (!isValid) {
      this._markPendingError(file, 'No active session');
      this.syncStatusPolling();
      return;
    }

    this.isUploading = true;
    try {
      await this.uploadService.uploadFile(file, { name: displayName, note });
      await this.loadFiles();

      this._amplitudeService.trackEvent('Upload_Save', {
        status: 'success',
        file_count: this.uploadedFiles.length,
        user_id: this.getUserId()
      });
      this.notificationService.notify('File uploaded successfully');
      this.pendingUploads = this.pendingUploads.filter(item => item.originalFile !== file);
      this.syncStatusPolling();
    } catch (error: any) {
      console.error('Upload error:', error);
      this.notificationService.notify(error.message || 'Error uploading file');
      this._markPendingError(file, error.message || 'Upload failed');
      this.syncStatusPolling();
    } finally {
      this.isUploading = false;
    }
  }

  private _markPendingError(file: File, message: string): void {
    this.pendingUploads = this.pendingUploads.map(item =>
      item.originalFile === file
        ? {
            ...item,
            status: 'error',
            uploadError: message,
            isUploading: false,
            note: 'Upload failed. Try again.'
          }
        : item
    );
  }

  async deleteFile(): Promise<void> {
    if (this.fileToDelete?.id) {
      try {
        // Track delete event
        this._amplitudeService.trackEvent('Upload_Delete', {
          file_name: this.fileToDelete.name,
          user_id: this.getUserId()
        });

        await this.uploadService.deleteFile(this.fileToDelete.id);
        await this.loadFiles();
        this.notificationService.notify('File deleted successfully');
      } catch (error: any) {
        console.error('Delete error:', error);
        this.notificationService.notify(error.message || 'Error deleting file');
      } finally {
        this.cancelDelete();
      }
    }
  }

  confirmDelete(file: FileMetadata): void {
    this.fileToDelete = file;
    this.showDeleteConfirmation = true;
  }

  cancelDelete(): void {
    this.fileToDelete = null;
    this.showDeleteConfirmation = false;
  }

  async downloadFile(file: FileMetadata): Promise<void> {
    try {
      const blob = await this.uploadService.downloadFile(file.id!);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      this.notificationService.notify('File downloaded successfully');
    } catch (error: any) {
      console.error('Download error:', error);
      this.notificationService.notify(error.message || 'Error downloading file');
    }
  }



  private getUserId(): string {
    const userInfo = sessionStorage.getItem('userInfo');
    if (userInfo) {
      try {
        const parsedUserInfo = JSON.parse(userInfo);
        return parsedUserInfo.user?.id || parsedUserInfo.id || 'anonymous';
      } catch (error) {
        return 'anonymous';
      }
    }
    return 'anonymous';
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + sizes[i];
  }

  getStatusClass(status: string): string {
    if (status?.toLowerCase().includes('error') || status?.toLowerCase().includes('failed')) {
      return 'status-failed';
    }
    switch (status?.toLowerCase()) {
      case 'uploaded': return 'status-pending';
      case 'processing': return 'status-processing';
      case 'successfully processed': return 'status-completed';
      case 'completed': return 'status-completed';
      default: return 'status-pending';
    }
  }

  private syncStatusPolling(): void {
    const hasPending =
      this.uploadedFiles.some(file => this.isPendingStatus(file.status)) ||
      this.pendingUploads.some(file => this.isPendingStatus(file.status));

    if (hasPending) {
      if (!this.statusPolling || this.statusPolling.closed) {
        this.statusPolling = interval(this.statusPollInterval).subscribe(() => {
          if (this.authService.isAuthenticated()) {
            void this.loadFiles();
          }
        });
      }
    } else if (this.statusPolling) {
      this.statusPolling.unsubscribe();
      this.statusPolling = undefined;
    }
  }

  private isPendingStatus(status?: string): boolean {
    if (!status) {
      return true;
    }

    const normalized = status.toLowerCase();
    if (normalized.includes('error') || normalized.includes('failed')) {
      return false;
    }

    return !['completed', 'successfully processed'].includes(normalized);
  }

  retryPendingUpload(pending: FileMetadata): void {
    if (!pending.originalFile) {
      return;
    }

    const file = pending.originalFile;
    const defaultNote = pending.originalNote || 'Uploaded via quick upload';
    const sizeLabel = this.formatFileSize(pending.size || file.size || 0);

    this._amplitudeService.trackEvent('Upload_Retry', {
      file_name: pending.name,
      user_id: this.getUserId()
    });

    this.pendingUploads = this.pendingUploads.map(item =>
      item === pending
        ? {
            ...item,
            status: 'uploading',
            note: `Retrying upload... (${sizeLabel})`,
            uploadError: undefined,
            isUploading: true
          }
        : item
    );

    this.syncStatusPolling();
    void this._performUpload(file, pending.name, defaultNote);
  }

  dismissPendingError(pending: FileMetadata): void {
    this._amplitudeService.trackEvent('Upload_DismissError', {
      file_name: pending.name,
      user_id: this.getUserId()
    });

    this.pendingUploads = this.pendingUploads.filter(item => item !== pending);
    this.syncStatusPolling();
  }

  // --- Web Sources Logic ---
  get isFreeEmailDomain(): boolean {
    if (!this.userDefaultDomain) return false;
    return this.FREE_EMAIL_DOMAINS.includes(this.userDefaultDomain.toLowerCase());
  }

  private async loadWebSources(): Promise<void> {
    try {
      const bcConfig = await this.userConfigService.loadBattleCardDomains();
      if (bcConfig.domains.length > 0) {
        this.webSources = [...new Set([...this.webSources, ...bcConfig.domains])];
      }

      const defaultDomain = this.userDefaultDomain;
      const shouldAddDefaultDomain =
        !!defaultDomain &&
        !bcConfig.ignoreDefault &&
        !this.isFreeEmailDomain;

      if (shouldAddDefaultDomain && defaultDomain && !this.webSources.includes(defaultDomain)) {
        this.webSources.push(defaultDomain);
      }
    } catch (err) {
      console.error('Error loading web sources', err);
    }
  }

  toggleFreeEmailTooltip(): void {
    this.showFreeEmailTooltip = !this.showFreeEmailTooltip;
  }

  closeFreeEmailTooltip(): void {
    this.showFreeEmailTooltip = false;
  }

  async addWebSource(): Promise<void> {
    if (!this.newWebSource.trim() || this.isSavingSources) return;

    let source = this.newWebSource.trim().toLowerCase();
    source = source.replace(/^(https?:\/\/)?(www\.)?/, '');
    source = source.split('/')[0];

    if (!source || this.webSources.includes(source)) {
      this.newWebSource = '';
      return;
    }

    this.isSavingSources = true;
    try {
      this.webSources.push(source);
      await this._saveSources();
      this.newWebSource = '';
      this._amplitudeService.trackEvent('Upload_AddWebSource', { domain: source });
    } catch (err) {
      this.webSources.pop();
      this.notificationService.notifyError('Failed to save URL');
    } finally {
      this.isSavingSources = false;
    }
  }

  async removeWebSource(index: number): Promise<void> {
    if (this.isSavingSources) return;
    const sourceToRemove = this.webSources[index];
    
    this.isSavingSources = true;
    const oldSources = [...this.webSources];
    try {
      this.webSources.splice(index, 1);
      await this._saveSources();
      this._amplitudeService.trackEvent('Upload_RemoveWebSource', { domain: sourceToRemove });
    } catch (err) {
      this.webSources = oldSources;
      this.notificationService.notifyError('Failed to remove URL');
    } finally {
      this.isSavingSources = false;
    }
  }

  private async _saveSources(): Promise<void> {
    const uniqueDomains = [...new Set(this.webSources)];
    await this.userConfigService.saveBattleCardDomains(false, uniqueDomains);
  }
}
